import Color from "../../style/enum/color"
import { getImageElement } from "./get-image-element"
import { getImageOrientation } from "./get-image-orientation"

export const ImageTransform = async (
  imageFile: File,
  maximumDimension: number,
): Promise<string> => {
  const base64string = await base64stringFromFile(imageFile)
  const image: HTMLImageElement = await getImageElement(base64string)
  const canvas = document.createElement("canvas")
  return transform(canvas, image, {
    scale: Math.min(
      1.0,
      calcResizedScale(image.width, image.height, maximumDimension),
    ),
    orientation: getImageOrientation(base64string),
  })
}

const base64stringFromFile = (file: File): Promise<string> => {
  return new Promise(resolve => {
    const reader = new FileReader()
    reader.addEventListener(
      "load",
      () => resolve(reader.result as string),
      false,
    )
    reader.readAsDataURL(file)
  })
}

const calcResizedScale = (
  currentWidth: number,
  currentHeight: number,
  maximumDimension: number,
): number => {
  if (currentWidth <= maximumDimension && currentHeight <= maximumDimension) {
    return 1.0
  }

  if (currentWidth > currentHeight) {
    return maximumDimension / currentWidth
  } else {
    return maximumDimension / currentHeight
  }
}

interface ITransformOptions {
  scale: number
  orientation: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | null
}

export const transform = (
  canvas: HTMLCanvasElement,
  image: HTMLImageElement,
  options: ITransformOptions,
): string => {
  const ctx = canvas.getContext("2d") as CanvasRenderingContext2D

  // サイズ計算
  const dWidth = Math.max(image.width * options.scale, 1)
  const dHeight = Math.max(image.height * options.scale, 1)

  // 方向補正で縦横が逆転する場合はcanvasの縦横のサイズも逆転させる
  if (options.orientation && options.orientation >= 5) {
    canvas.width = dHeight
    canvas.height = dWidth
  } else {
    canvas.width = dWidth
    canvas.height = dHeight
  }

  // 方向補正
  if (options.orientation) {
    switch (options.orientation) {
      case 2:
        ctx.transform(-1, 0, 0, 1, dWidth, 0)
        break
      case 3:
        ctx.transform(-1, 0, 0, -1, dWidth, dHeight)
        break
      case 4:
        ctx.transform(1, 0, 0, -1, 0, dHeight)
        break
      case 5:
        ctx.transform(0, 1, 1, 0, 0, 0)
        break
      case 6:
        ctx.transform(0, 1, -1, 0, dHeight, 0)
        break
      case 7:
        ctx.transform(0, -1, -1, 0, dHeight, dWidth)
        break
      case 8:
        ctx.transform(0, -1, 1, 0, 0, dWidth)
        break
    }
  }

  // 透過画像の背景を白にするため
  ctx.fillStyle = Color.WHITE
  ctx.fillRect(0, 0, dWidth, dHeight)

  ctx.drawImage(image, 0, 0, image.width, image.height, 0, 0, dWidth, dHeight)
  return canvas.toDataURL("image/jpeg")
}
